/**
 * Trial Limit Service
 * Manages trial call usage limits using localStorage
 * Works across web and Capacitor (iOS/Android) apps
 * Limit is now stored in the trial assistant configuration in the database
 */

import { supabase } from '../lib/supabase';

const STORAGE_KEY = 'trial_call_count';
let cachedLimit = null;

/**
 * Get the current trial call count
 * @returns {number} Number of trial calls used
 */
export const getTrialCallCount = () => {
  try {
    const count = localStorage.getItem(STORAGE_KEY);
    return count ? parseInt(count, 10) : 0;
  } catch (error) {
    console.error('Error reading trial call count:', error);
    return 0;
  }
};

/**
 * Increment the trial call count
 * @returns {number} New trial call count
 */
export const incrementTrialCallCount = () => {
  try {
    const currentCount = getTrialCallCount();
    const newCount = currentCount + 1;
    localStorage.setItem(STORAGE_KEY, newCount.toString());
    console.log(`Trial call count incremented: ${currentCount} → ${newCount}`);
    return newCount;
  } catch (error) {
    console.error('Error incrementing trial call count:', error);
    return getTrialCallCount();
  }
};

/**
 * Check if user has reached trial call limit
 * Uses cached limit if available, otherwise returns false
 * For up-to-date limit checking, use hasReachedTrialLimitAsync instead
 * @returns {boolean} True if limit reached
 */
export const hasReachedTrialLimit = () => {
  const count = getTrialCallCount();
  // Use cached limit if available, otherwise assume limit not reached
  // (caller should use async version for accurate check)
  if (cachedLimit === null) {
    console.warn('Trial limit not cached. Use hasReachedTrialLimitAsync for accurate check.');
    return false;
  }
  return count >= cachedLimit;
};

/**
 * Get the configured trial call limit from the database
 * @returns {Promise<number>} Maximum number of trial calls allowed
 */
export const getTrialLimit = async () => {
  try {
    // Return cached value if available
    if (cachedLimit !== null) {
      return cachedLimit;
    }

    // Fetch trial assistant configuration from database
    const { data, error } = await supabase
      .from('vapi_assistants')
      .select('trial_call_limit')
      .eq('is_trial', true)
      .eq('is_active', true)
      .single();

    if (error) {
      console.error('Error fetching trial limit:', error);
      cachedLimit = 1; // Default fallback
      return cachedLimit;
    }

    cachedLimit = data?.trial_call_limit || 1;
    return cachedLimit;
  } catch (error) {
    console.error('Error in getTrialLimit:', error);
    return 1; // Default fallback
  }
};

/**
 * Get remaining trial calls
 * @returns {Promise<number>} Number of trial calls remaining
 */
export const getRemainingTrialCalls = async () => {
  const count = getTrialCallCount();
  const limit = await getTrialLimit();
  return Math.max(0, limit - count);
};

/**
 * Check if user has reached trial call limit
 * Async version that fetches limit from database
 * @returns {Promise<boolean>} True if limit reached
 */
export const hasReachedTrialLimitAsync = async () => {
  const count = getTrialCallCount();
  const limit = await getTrialLimit();
  return count >= limit;
};

/**
 * Reset trial call count (for testing/debugging only)
 * Should not be exposed in production UI
 */
export const resetTrialCallCount = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    cachedLimit = null; // Clear cache
    console.log('Trial call count reset');
  } catch (error) {
    console.error('Error resetting trial call count:', error);
  }
};

/**
 * Clear the cached limit (call when trial assistant config changes)
 */
export const clearLimitCache = () => {
  cachedLimit = null;
};

export default {
  getTrialCallCount,
  incrementTrialCallCount,
  hasReachedTrialLimit,
  hasReachedTrialLimitAsync,
  getTrialLimit,
  getRemainingTrialCalls,
  resetTrialCallCount,
  clearLimitCache
};
